import React from "react";
import { GlassCard } from "../components/GlassCard";
import { SectionLabel } from "../components/SectionLabel";
import { SkillBar } from "../components/SkillBar";

export const SkillsPage = ({ c, isDark }) => {
  const bgDot = isDark
    ? "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)"
    : "radial-gradient(rgba(0,0,50,0.06) 1px, transparent 1px)";

  const skillGroups = [
    { icon: "web", color: c.primary, title: "Frontend", tags: ["React 18", "TypeScript", "Next.js", "Tailwind CSS"], label: "Core Logic", years: "6+ Years", percent: 92 },
    { icon: "dns", color: c.secondary, title: "Backend", tags: ["Node.js", "Go", "Python", "GraphQL"], label: "Server-Side Arch", years: "5+ Years", percent: 88 },
    { icon: "storage", color: c.primaryContainer, title: "Databases", tags: ["PostgreSQL", "Redis", "MongoDB", "ElasticSearch"], label: "Data Persistence", years: "4+ Years", percent: 82 },
    { icon: "terminal", color: c.outline, title: "DevOps", tags: ["Docker", "Kubernetes", "Terraform", "GitHub Actions"], label: "Automation & CI/CD", years: "3+ Years", percent: 75 },
    { icon: "cloud", color: c.primary, title: "Cloud", tags: ["AWS", "GCP", "Serverless", "Cloudflare"], label: "Cloud Computing", years: "4+ Years", percent: 80 },
    { icon: "architecture", color: c.secondary, title: "Architecture", tags: ["Microservices", "Event-Driven", "RESTful API", "DDD"], label: "System Design", years: "3+ Years", percent: 70 },
  ];

  const mastery = [
    { label: "System Architecture", level: "EXPERT", bars: 4, color: c.primary },
    { label: "Data Modeling", level: "ADVANCED", bars: 3, color: c.secondary },
    { label: "Frontend Engineering", level: "EXPERT", bars: 4, color: c.primaryContainer },
    { label: "DevOps & CI/CD", level: "PROFICIENT", bars: 3, color: c.outline },
  ];

  return (
    <div style={{ background: c.background, minHeight: "100vh", backgroundImage: bgDot, backgroundSize: "32px 32px", paddingTop: 120 }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "48px 64px 120px" }}>
        <header style={{ marginBottom: 64 }}>
          <SectionLabel c={c}>Expertise Inventory</SectionLabel>
          <h1 style={{
            fontFamily: "'Geist', sans-serif", fontWeight: 700, lineHeight: 1.1,
            fontSize: "clamp(36px, 5vw, 64px)", color: c.onSurface, letterSpacing: "-0.02em", marginBottom: 16,
          }}>
            Technical{" "}
            <span style={{ background: `linear-gradient(135deg, ${c.primary}, ${c.secondary}, ${c.primaryContainer})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Capabilities.
            </span>
          </h1>
          <p style={{ fontFamily: "'Geist', sans-serif", fontSize: 18, color: c.onSurfaceVariant, maxWidth: 560, lineHeight: 1.6 }}>
            A comprehensive mapping of full-stack engineering proficiency, specialized in building distributed systems and high-performance user interfaces.
          </p>
        </header>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 32, marginBottom: 80 }}>
          {skillGroups.map(({ icon, color, title, tags, label, years, percent }) => (
            <GlassCard key={title} isDark={isDark} style={{ borderRadius: 16, padding: 32, transition: "all 0.5s", cursor: "pointer" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 32 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 8,
                  background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <span className="material-symbols-outlined" style={{ color }}>{icon}</span>
                </div>
                <h2 style={{ fontFamily: "'Geist', sans-serif", fontSize: 22, fontWeight: 600, color: c.onSurface }}>{title}</h2>
              </div>

              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 32 }}>
                {tags.map(t => (
                  <span key={t} style={{
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
                    background: isDark ? c.surfaceContainerHighest : "#e8eaf8",
                    color: c.onSurfaceVariant, padding: "4px 12px", borderRadius: 9999,
                    border: `1px solid ${isDark ? "rgba(66,71,84,0.3)" : "rgba(0,0,0,0.06)"}`,
                  }}>{t}</span>
                ))}
              </div>

              <SkillBar label={label} years={years} percent={percent} color={color} c={c} isDark={isDark} />
            </GlassCard>
          ))}
        </div>

        <GlassCard isDark={isDark} style={{ borderRadius: 20, overflow: "hidden", padding: 4 }}>
          <div style={{
            background: isDark ? c.surfaceContainerLowest : "#ffffff",
            borderRadius: 16, padding: "48px 64px",
          }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}>
              <div>
                <h3 style={{ fontFamily: "'Geist', sans-serif", fontSize: 28, fontWeight: 600, color: c.onSurface, marginBottom: 32 }}>Mastery Distribution</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                  {mastery.map(({ label, level, bars, color }) => (
                    <div key={label}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 12 }}>
                        <h4 style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: c.onSurface }}>{label}</h4>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color, fontWeight: 700 }}>{level}</span>
                      </div>
                      <div style={{ display: "flex", gap: 4, height: 12 }}>
                        {[...Array(5)].map((_, i) => (
                          <div key={i} style={{
                            flex: 1, borderRadius: 2,
                            background: i < bars ? color : (isDark ? c.surfaceContainerHighest : "#e0e4f2"),
                          }} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ position: "relative", height: 320, borderRadius: 12, overflow: "hidden", border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}` }}>
                <img
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuAQq8gr6eBv6Pv62flfH9XG65vI8SKVsLu7VFWVB7sXyXd0zaFvG9JuspCCfI06b2v04QYcYf51C3y01bOFVyvCpYCkBITLiT8rkk4EcV3zccKsX3Tm-WWBzP_IGSOQRTpA_1VistX5WFFUqeapvJW8za57OXDMQZBtYvI4rRTL8-FW3SGkCn3WY-ljezV1MEUOd5nkWB8BbXlJruIF7RjWw_gBXt-L_O9HLZvBwkiRVzeSH-LduHLi2enA8n8CZ_F4vl8rmouim7Q"
                  alt="Circuit board" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.6 }}
                />
                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${c.background}, transparent)` }} />
                <div style={{ position: "absolute", bottom: 24, left: 24, right: 24 }}>
                  <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: c.primary, marginBottom: 4 }}>LAST_UPDATE: 2024.11.05</p>
                  <div style={{ fontFamily: "'Geist', sans-serif", fontSize: 20, fontWeight: 600, color: c.onSurface }}>Continuous Integration Active</div>
                </div>
              </div>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
};